import { useMemo, useState } from 'react';
import {
  gatewayIdForVerseEnv,
  gatewayUrlOf,
  getVersesByEnv,
  type VerseEntry,
  type VerseFlavor,
} from '@rb/shared/admin-verses';
import { splitByFlavor, type RevenueSplit } from '@rb/shared/analytics/revenue-split';
import {
  DailyTrendCard,
  ProductRevenueCard,
  RevenueKpiRow,
  TopPayersCard,
} from '@/components/admin/ShopRevenueTables';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { readAuthToken } from '@/auth/auth-token';
import { fetchAllSales, type VxSale } from '@/client/vxshop.client';
import { downloadCsv } from '@/lib/download';
import { nicknameKey, type NicknameTarget } from '@/shop/game-nickname';
import { aggregateSales, type RevenueAgg } from '@/shop/revenue-agg';
import {
  filterByPeriod,
  isWiderPeriod,
  PERIOD_OPTIONS,
  type Period,
  periodStartMs,
} from '@/shop/revenue-period';
import { useGameNicknames } from '@/shop/use-game-nicknames';
import { useVerseOwnership } from '@/shop/use-verse-ownership';

type Phase = 'idle' | 'loading' | 'done' | 'error';
type VerseChoice = string | 'all'; // verseId 또는 '합산'
type EnvChoice = 'prod' | 'staging';

/** 어느 verse(web2/web3) 에서 온 결제인지 태그를 붙인 sale. 합산 분해·닉네임 조회의 근거. */
type FlavoredSale = VxSale & { flavor: VerseFlavor; verseAlias: string; verseId: string };

const VERSES_BY_ENV: Record<EnvChoice, readonly VerseEntry[]> = {
  prod: getVersesByEnv('prod'),
  staging: getVersesByEnv('staging'),
};

export default function ShopRevenuePage() {
  const { account, owned } = useVerseOwnership();
  const [env, setEnv] = useState<EnvChoice>('prod');
  const [verseChoice, setVerseChoice] = useState<VerseChoice>('all');
  const [period, setPeriod] = useState<Period>('7d');
  /** 마지막으로 페치를 돌린 기간 — 더 넓은 기간을 고르면 데이터가 모자라 재조회가 필요하다. */
  const [loadedPeriod, setLoadedPeriod] = useState<Period | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<{ page: number; total: number } | null>(null);
  const [error, setError] = useState('');
  const [raw, setRaw] = useState<FlavoredSale[]>([]);

  const verses = VERSES_BY_ENV[env];

  const targetVerses = useMemo<VerseEntry[]>(
    () => (verseChoice === 'all' ? [...verses] : verses.filter((v) => v.verseId === verseChoice)),
    [verseChoice, verses]
  );

  // prod 게이트웨이는 dev/staging 테스트 결제가 섞일 수 있어 stage==='PROD' 만 매출에 포함한다.
  // staging 게이트웨이(staging.v8-meme-api.verse8.io)는 endpoint 가 이미 분리돼 있고,
  // 반환 레코드의 stage 값이 실제로는 'PROD' 로 내려오는 케이스가 있어 stage 필터를 생략하고
  // 게이트웨이 응답을 그대로 신뢰한다.
  const stageFiltered = useMemo(
    () => (env === 'prod' ? raw.filter((s) => s.stage === 'PROD') : raw),
    [raw, env]
  );

  // 합산 집계는 두 verse 를 합쳐 payer 를 중복 제거하고, split 은 같은 모수를 web2/web3 로 쪼갠다
  // (합계 = web2 + web3 가 항상 성립).
  const scoped = useMemo(() => filterByPeriod(stageFiltered, period), [stageFiltered, period]);
  const agg: RevenueAgg = useMemo(() => aggregateSales(scoped), [scoped]);
  const split: RevenueSplit = useMemo(() => splitByFlavor(scoped), [scoped]);
  // 두 verse 를 함께 불러온 경우에만 분해 열을 노출한다(단일 verse 조회면 의미 없음).
  const showSplit = split.flavorsPresent.length > 1;

  const disputeCount = useMemo(
    () => scoped.filter((s) => s.disputeStatus != null).length,
    [scoped]
  );

  // 페치는 기간 경계에서 멈추므로, 불러온 것보다 넓은 기간을 고르면 표가 과소집계된다.
  const needsReload = loadedPeriod != null && isWiderPeriod(period, loadedPeriod);

  // 닉네임은 payer 가 속한 verse 의 게임 상태에만 있으므로 uid → verseId 를 결제에서 얻는다.
  const payerVerseId = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of scoped) if (!m.has(s.userUid)) m.set(s.userUid, s.verseId);
    return m;
  }, [scoped]);

  const nicknameTargets = useMemo<NicknameTarget[]>(
    () =>
      agg.topPayers
        .map((p) => ({ verseId: payerVerseId.get(p.uid) ?? '', wallet: p.wallet }))
        .filter((t) => t.verseId && t.wallet),
    [agg.topPayers, payerVerseId]
  );

  const gameNicknames = useGameNicknames({
    targets: nicknameTargets,
    account,
    gatewayUrl: gatewayUrlOf(gatewayIdForVerseEnv(env)),
  });

  const handleExportCsv = () => {
    // 화면 표와 같은 모수(기간 적용 후)를 내보낸다.
    const rows = scoped.map((s) => ({
      verse: s.verseAlias,
      flavor: s.flavor,
      id: s.id,
      userUid: s.userUid,
      shopItemId: s.shopItemId,
      quantity: s.quantity,
      unitPrice: s.unitPrice,
      totalPrice: s.totalPrice,
      status: s.status,
      createdAt: s.createdAt,
      refundedAt: s.refundedAt ?? '',
      disputeStatus: s.disputeStatus ?? '',
      disputeResolvedAt: s.disputeResolvedAt ?? '',
      stage: s.stage,
      'item.productId': s.item.productId,
      'item.name': s.item.name,
      'buyer.walletAddress': s.buyer.walletAddress ?? '',
      'buyer.displayName': s.buyer.displayName ?? '',
      'buyer.handle': s.buyer.handle ?? '',
      'buyer.name': s.buyer.name ?? '',
    }));
    const columns = [
      { key: 'verse', header: 'verse' },
      { key: 'flavor', header: 'flavor' },
      { key: 'id', header: 'id' },
      { key: 'userUid', header: 'userUid' },
      { key: 'shopItemId', header: 'shopItemId' },
      { key: 'quantity', header: 'quantity' },
      { key: 'unitPrice', header: 'unitPrice (VX)' },
      { key: 'totalPrice', header: 'totalPrice (VX)' },
      { key: 'status', header: 'status' },
      { key: 'createdAt', header: 'createdAt' },
      { key: 'refundedAt', header: 'refundedAt' },
      { key: 'disputeStatus', header: 'disputeStatus' },
      { key: 'disputeResolvedAt', header: 'disputeResolvedAt' },
      { key: 'stage', header: 'stage' },
      { key: 'item.productId', header: 'item.productId' },
      { key: 'item.name', header: 'item.name' },
      { key: 'buyer.walletAddress', header: 'buyer.walletAddress' },
      { key: 'buyer.displayName', header: 'buyer.displayName' },
      { key: 'buyer.handle', header: 'buyer.handle' },
      { key: 'buyer.name', header: 'buyer.name' },
    ];
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(`shop-revenue-${env}-${period}-${today}.csv`, rows, columns);
  };

  const handleLoad = async () => {
    const token = readAuthToken();
    if (!token) {
      setError('로그인 토큰이 없습니다. 다시 로그인하세요.');
      setPhase('error');
      return;
    }
    setPhase('loading');
    setError('');
    setProgress(null);
    setRaw([]);
    const gatewayId = gatewayIdForVerseEnv(env);
    // 최신 페이지부터 읽다가 이 시각 이전 레코드를 만나면 멈춘다('전체'면 null → 전량 페치).
    const sinceMs = periodStartMs(period);
    const fetchedPeriod = period;
    try {
      const all: FlavoredSale[] = [];
      for (const verse of targetVerses) {
        const sales = await fetchAllSales(
          { token, verseId: verse.verseId, gatewayId },
          (p) => setProgress({ page: p.page, total: all.length + p.total }),
          { sinceMs }
        );
        // verse 별 페치 시점에만 flavor 를 알 수 있으므로 여기서 태깅한다.
        all.push(
          ...sales.map((s) => ({
            ...s,
            flavor: verse.flavor,
            verseAlias: verse.alias,
            verseId: verse.verseId,
          }))
        );
      }
      setRaw(all);
      setLoadedPeriod(fetchedPeriod);
      setPhase('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPhase('error');
    }
  };

  const loading = phase === 'loading';

  return (
    <section className="flex flex-col gap-4">
      <header>
        <h2 className="text-2xl font-semibold tracking-tight">Shop Revenue</h2>
        <p className="text-sm text-muted-foreground">
          VX 결제 sales API 기준 $ 매출 대시보드. 100 VX = $1. 순매출/payers/ARPPU 는 환불 제외
          기준입니다. 합산 조회 시 모든 표가 web2/web3/합계로 분해됩니다. 기간은 오늘 포함 N 일(로컬
          자정 기준)이며, 최신 결제부터 읽다가 기간을 벗어나면 페치를 멈춥니다 — '전체'만 전량
          페치라 느립니다.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">조회 설정</CardTitle>
          <CardDescription>
            prod 또는 staging verse 별/합산 매출을 불러옵니다. 합산은 두 verse 를 합쳐 payer 를 중복
            제거합니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Env</span>
            <Select
              value={env}
              onValueChange={(v) => {
                setEnv(v as EnvChoice);
                setVerseChoice('all'); // env 전환 시 verse 선택 초기화
              }}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="prod">prod</SelectItem>
                <SelectItem value="staging">staging</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Verse</span>
            <Select value={verseChoice} onValueChange={(v) => setVerseChoice(v as VerseChoice)}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">합산 ({env} 전체)</SelectItem>
                {verses.map((v) => (
                  <SelectItem key={v.verseId} value={v.verseId}>
                    {v.alias}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">기간</span>
            <Select value={period} onValueChange={(v) => setPeriod(v as Period)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button type="button" onClick={handleLoad} disabled={loading}>
            {loading ? '불러오는 중…' : '매출 불러오기'}
          </Button>

          {phase === 'done' && (
            <Button type="button" variant="outline" onClick={handleExportCsv}>
              CSV 다운로드 ({scoped.length.toLocaleString()}건)
            </Button>
          )}

          {loading && progress && (
            <span className="text-sm text-muted-foreground">
              page {progress.page} · {progress.total.toLocaleString()}건
            </span>
          )}
        </CardContent>
      </Card>

      {!owned && account && (
        <Alert>
          <AlertTitle>참고</AlertTitle>
          <AlertDescription>
            현재 활성 verse 의 소유 계정이 아닙니다. 매출 조회는 verse8 JWT 로 직접 페치하므로 계속
            진행됩니다. (로그인 계정: <span className="font-mono">{account}</span>)
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription className="whitespace-pre-wrap break-words">{error}</AlertDescription>
        </Alert>
      )}

      {needsReload && !loading && (
        <Alert>
          <AlertTitle>기간을 넓혔습니다</AlertTitle>
          <AlertDescription>
            현재 표는 직전에 불러온 기간(
            {PERIOD_OPTIONS.find((o) => o.value === loadedPeriod)?.label}) 데이터라 과소집계입니다.
            "매출 불러오기" 를 다시 눌러 주세요.
          </AlertDescription>
        </Alert>
      )}

      {phase === 'done' && (
        <>
          <RevenueKpiRow agg={agg} split={split} showSplit={showSplit} />

          {disputeCount > 0 && (
            <Alert variant="destructive">
              <AlertTitle>분쟁 주의</AlertTitle>
              <AlertDescription>
                disputeStatus 가 있는 건 {disputeCount.toLocaleString()}건 (차지백 리스크).
              </AlertDescription>
            </Alert>
          )}

          <DailyTrendCard agg={agg} split={split} showSplit={showSplit} />

          <ProductRevenueCard agg={agg} split={split} showSplit={showSplit} />

          <TopPayersCard
            agg={agg}
            split={split}
            showSplit={showSplit}
            nickname={{
              of: (uid, wallet) =>
                gameNicknames.nicknames.get(nicknameKey(payerVerseId.get(uid) ?? '', wallet)) ??
                null,
              loading: gameNicknames.phase === 'loading',
              progress: gameNicknames.progress,
              error: gameNicknames.error,
              failed: gameNicknames.failed,
              retry: gameNicknames.retry,
            }}
          />

          <p className="text-xs text-muted-foreground">
            {env === 'prod' ? (
              <>
                <Badge variant="outline">PROD only</Badge> prod 게이트웨이 응답 중 stage 가 PROD 인
                건만 집계 (테스트 결제 제외)
              </>
            ) : (
              <>
                <Badge variant="outline">staging gateway</Badge> staging 게이트웨이 응답 전체 집계
                (stage 필드 무시)
              </>
            )}
          </p>
        </>
      )}
    </section>
  );
}
